/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useCallback, useContext, useEffect } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import Blaze from "@swiggy-private/blaze.js";
import { ICartItem } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import {
    AnalyticsContext,
    IApiConfigs,
    ResponseContext,
    ScreenNames,
    SwiggyContextHeader,
    TableContext,
} from "@swiggy-private/portal-dineout-context";
import { ErrorComponent, MenuUI } from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { TabType } from "types";
import { CurtainOverlayLoader } from "./CurtainOverlayLoader";
import { MenuContainer } from "./styled";
import { PageName, RECORD_PERCIEVED_TIME_EVENTS, ROUTES } from "../../constants";
import { useCrossSellActions } from "../../hooks/useCrossSellActions";
import { useFetchCrossSellConfig } from "../../hooks/useFetchCrossSellConfig";
import useFilters from "../../hooks/useFilters";
import { useMenuData } from "../../hooks/useMenuData";
import { usePinInjection } from "../../hooks/usePinInjection";
import { usePLError } from "../../hooks/usePLError";
import { useQuickActions } from "../../hooks/useQuickActions";
import { withPinAuth } from "../PinAuth";

export interface MenuProps {
    setHasBottomBar: React.Dispatch<React.SetStateAction<boolean>>;
    genericClickHandler: (_card?: ICartItem, extraParams?: Record<string, any>) => void;
    isCurtainRaiserShown: boolean;
    isPinLoading?: boolean;
}

function Menu({ setHasBottomBar, genericClickHandler, isCurtainRaiserShown, isPinLoading }: MenuProps) {
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { tableId = "", restaurantId = "" } = useContext(TableContext);
    const { state } = useContext(ResponseContext);
    const router = useRouter();
    const { onEmptyPage, onInvalidWidget, onWidgetError } = usePLError(PageName.MENU);
    const { onQuickActionButtonClick } = useQuickActions({ skipPinCheck: true });

    const hideBottomBar = useCallback(() => {
        setHasBottomBar(false);
    }, [setHasBottomBar]);

    const { loading, showApiError } = useMenuData(hideBottomBar);
    usePinInjection();

    const onMenuLoad = useCallback(() => {
        return Blaze.recordEnd(RECORD_PERCIEVED_TIME_EVENTS.APP_START, {
            headers: JSON.stringify(swiggyHeaders),
            restaurantId,
        });
    }, [restaurantId, swiggyHeaders]);

    const { applyFilters } = useFilters();
    const { fetchCrossSellConfig } = useFetchCrossSellConfig();
    const { onCrossSellShown } = useCrossSellActions();

    useEffect(() => {
        fetchCrossSellConfig();
    }, [fetchCrossSellConfig]);

    const clickHandler = useCallback(
        (_card?: ICartItem, extraParams?: Record<string, any>) => {
            genericClickHandler(_card, {
                ...extraParams,
                onQuickActionButtonClick: onQuickActionButtonClick,
                applyFilters,
            });
        },
        [genericClickHandler, onQuickActionButtonClick, applyFilters],
    );

    const ifCartItemAvailable = state.cart.localCart.cartItems.length > 0;
    const goToCart = () => router?.push(`${ROUTES.CART_ORDERS}?tab=${TabType.CART}`);

    if (showApiError) {
        return (
            <ErrorComponent
                heading="Something went wrong"
                description="Our best minds are on this, please refresh or come back later"
                ctaText="Refresh"
                onCtaClick={() => {
                    window.location.reload();
                }}
                showFullPage={true}
                ctaBtnType="primary"
            />
        );
    }

    if (isEmpty(tableId) || isEmpty(restaurantId)) {
        return null;
    }

    if (loading || !isCurtainRaiserShown || isPinLoading) {
        return <CurtainOverlayLoader />;
    }

    const popupConfigs = state.menu?.configs as IApiConfigs;

    return (
        <MenuContainer>
            <MenuUI
                genericClickHandler={clickHandler}
                ifCartItemAvailable={ifCartItemAvailable}
                goToCart={goToCart}
                onInvalidWidget={onInvalidWidget}
                onEmptyPage={onEmptyPage}
                onWidgetError={onWidgetError}
                onMenuLoad={onMenuLoad}
                impressionAnalytics={impressionEvent}
                clickAnalytics={clickEvent}
                popupConfigs={popupConfigs}
                screenName={ScreenNames.MENU}
                onCrossSellShown={onCrossSellShown}
            />
        </MenuContainer>
    );
}

export default memo(withPinAuth(Menu));
